import React, { useState, useEffect, useMemo } from 'react';
import Select from 'react-select';
import _ from 'lodash';
import { Card, CardHeader, CardBody } from 'reactstrap';
import TightTable from 'components/TightTable';
import TightChart from 'components/TightChart';
import TimePeriod, { formatDates } from 'components/TimePeriod';
import { AutoColumns } from 'components/Columns';
import { useArrayState } from 'components/CustomHooks';
import { optionizeValue, optionizeAll, getValue, getDate } from 'helpers/select';

function ValueAtRisk({ data: { data, table_dates }, options = {}, params, notify, session }) {
  const [varOptions, setVarOptions] = useState([]);
  const [selectedVarOption, setSelectedVarOption] = useState(null);
  const [holdingOptions, setHoldingOptions] = useState([]);
  const [selectedHoldingOption, setSelectedHoldingOption] = useState(null);

  useEffect(() => {
    if (!data) return;

    const _varOptions = optionizeAll(data.index);
    setVarOptions(_varOptions);

    if (_varOptions.length > 0) {
      setSelectedVarOption(_varOptions[0]);
    }

    const _holdings = data.columns.filter((val) => ![params.port1Name, params.benchmark].includes(val));
    const _holdingOptions = optionizeAll(_holdings);
    setHoldingOptions(_holdingOptions);

    if (_holdingOptions.length > 0) {
      setSelectedHoldingOption(_holdingOptions[0]);
    }
  }, [data]);

  return (
    <>
      <Card>
        <CardHeader tag="h4" className="mt-0 text-center">
          Value at Risk
          <div className="tw-text-sm">(95% Confidence)</div>
          <TimePeriod dates={table_dates} />
        </CardHeader>
        <CardBody>
          <TightTable
            suffix="risk-performance-var"
            data={data}
            indexName=""
            indexWidth="250px"
            dataType="percentage"
            useSort={true}
            flip={true}
            title={['Value at Risk', '(95% Confidence)', formatDates(table_dates)]}
            {...options.valueAtRiskTable}
          />
        </CardBody>
      </Card>

      <div className="tw-grid tw-grid-cols-6 tw-gap-4">
        <div className="tw-col-start-2 tw-col-span-4">
          <div className="tw-flex">
            <div className="tw-mr-1">
              <div className="tw-text-left tw-font-bold">SELECT VAR METRIC</div>
              <Select
                className="react-select tw-w-[200px] tw-inline-block"
                value={selectedVarOption}
                onChange={setSelectedVarOption}
                options={varOptions}
              />
            </div>

            {holdingOptions.length > 1 && (
              <div>
                <div className="tw-text-left tw-font-bold">SELECT HOLDING</div>
                <Select
                  className="react-select tw-w-[400px] tw-inline-block"
                  value={selectedHoldingOption}
                  onChange={setSelectedHoldingOption}
                  options={holdingOptions}
                />
              </div>
            )}
          </div>

          {selectedVarOption && (
            <>
              {selectedHoldingOption ? (
                <Card>
                  <CardHeader tag="h4" className="mt-0 text-center">
                    {selectedVarOption.label}
                  </CardHeader>
                  <CardBody>
                    <TightChart
                      title={selectedVarOption.label}
                      data={data}
                      rows={[selectedVarOption.value]}
                      cols={[params.port1Name, params.benchmark, selectedHoldingOption.value]}
                      wraplabelX={true}
                      dataType="percentage"
                    />
                  </CardBody>
                </Card>
              ) : (
                <Card>
                  <CardHeader tag="h4" className="mt-0 text-center">
                    {selectedVarOption.label}
                  </CardHeader>
                  <CardBody>
                    <TightChart
                      title={selectedVarOption.label}
                      data={data}
                      rows={[selectedVarOption.value]}
                      cols={[params.port1Name, params.benchmark]}
                      wraplabelX={true}
                      dataType="percentage"
                    />
                  </CardBody>
                </Card>
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
}

export default ValueAtRisk;
